import _ from 'lodash';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import WaveSurfer from 'wavesurfer.js';

const wavePlayerBarWidth = 1;
const wavePlayerCursorColor = '#ccc';
const wavePlayerHeight = 75;
const wavePlayerMediaType = 'audio';
const wavePlayerProgressColor = '#a7dba7';
const wavePlayerWaveColor = '#009b00';

const initialState = {
  recordingId: null,
  url: null,
  playing: false,
  currentPosition: 0,
  duration: 0,
  ready: false,
  playStartAt: null,
  isWebAudioSupported: true,
};

const AudioPlaybackContext = createContext(null);

/**
 * Hidden player component that renders via portal when audio needs to play
 * without a visible container (e.g., when scrolled off a virtualized list)
 */
function HiddenAudioPlayer({ containerRef }) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  if (!mounted) {
    return null;
  }

  return ReactDOM.createPortal(
    <div
      id="audio-playback-hidden-container"
      ref={containerRef}
      style={{
        position: 'absolute',
        left: '-9999px',
        width: '300px',
        height: '75px',
      }}
    />,
    document.body
  );
}

HiddenAudioPlayer.propTypes = {
  containerRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
};

export function AudioPlaybackProvider({ children }) {
  const [state, setState] = useState(initialState);
  const wavesurferRef = useRef(null);
  const hiddenContainerRef = useRef(null);
  const onReadyCallbackRef = useRef(null);
  const analyticsCallbackRef = useRef(null);
  const isAttachingRef = useRef(false);
  const [needsHiddenContainer, setNeedsHiddenContainer] = useState(false);

  const notifyAnalytics = useCallback(() => {
    if (analyticsCallbackRef.current && state.playStartAt) {
      const playDuration = new Date().getTime() - state.playStartAt;
      analyticsCallbackRef.current(playDuration);
    }
  }, [state.playStartAt]);

  const handleReady = useCallback(() => {
    const duration = wavesurferRef.current?.getDuration() || 0;
    setState(prev => ({
      ...prev,
      ready: true,
      duration,
      playing: true,
      playStartAt: new Date().getTime(),
    }));

    if (onReadyCallbackRef.current) {
      onReadyCallbackRef.current(duration);
    }

    wavesurferRef.current?.toggleInteraction();
    wavesurferRef.current?.play(0);
  }, []);

  const handleFinish = useCallback(() => {
    notifyAnalytics();
    setState(prev => ({
      ...prev,
      playing: false,
      currentPosition: prev.duration,
    }));
  }, [notifyAnalytics]);

  const handlePositionChange = useMemo(
    () =>
      _.throttle(
        position => {
          setState(prev => {
            if (prev.playing) {
              return { ...prev, currentPosition: Math.max(0, position) };
            }
            return prev;
          });
        },
        500,
        { leading: true, trailing: false }
      ),
    []
  );

  // Cleanup throttled function on unmount to cancel any pending timers
  useEffect(() => {
    return () => {
      handlePositionChange.cancel();
    };
  }, [handlePositionChange]);

  const handleSeek = useCallback(floatPosition => {
    if (!isNaN(floatPosition)) {
      setState(prev => ({
        ...prev,
        currentPosition: Math.max(0, floatPosition * prev.duration),
      }));
    }
  }, []);

  const createWaveSurfer = useCallback(
    (url, duration, isWebAudioSupported, container) => {
      const maxDuration = 3600;
      const useWebAudio = isWebAudioSupported && duration < maxDuration;

      const ws = WaveSurfer.create({
        audioContext: useWebAudio ? null : 'OfflineAudioContext',
        backend: useWebAudio ? 'WebAudio' : 'MediaElement',
        barWidth: wavePlayerBarWidth,
        container,
        cursorColor: wavePlayerCursorColor,
        height: wavePlayerHeight,
        interact: false,
        mediaType: wavePlayerMediaType,
        progressColor: wavePlayerProgressColor,
        responsive: 150,
        waveColor: wavePlayerWaveColor,
      });
      ws.load(url);
      ws.on('ready', handleReady);
      ws.on('finish', handleFinish);
      ws.on('audioprocess', handlePositionChange);
      ws.on('seek', handleSeek);

      return ws;
    },
    [handleReady, handleFinish, handlePositionChange, handleSeek]
  );

  const stop = useCallback(() => {
    if (wavesurferRef.current) {
      const ws = wavesurferRef.current;
      notifyAnalytics();
      try {
        ws.pause();
        ws.empty();
      } catch (e) {
        // ignore - instance may already be in a partially torn-down state
      }
      ws._onResize?.clear();
      ws.destroy();
      wavesurferRef.current = null;
    }

    setState(initialState);
    onReadyCallbackRef.current = null;
    analyticsCallbackRef.current = null;
    isAttachingRef.current = false;
    setNeedsHiddenContainer(false);
  }, [notifyAnalytics]);

  const play = useCallback(
    config => {
      const { url, recordingId, duration, isWebAudioSupported, container, onReady, analyticsCallback } = config;

      // If same recording and already playing, do nothing
      if (state.recordingId === recordingId && state.playing) {
        return;
      }

      // If same recording but paused, resume
      if (state.recordingId === recordingId && wavesurferRef.current && !state.playing) {
        setState(prev => ({
          ...prev,
          playing: true,
          playStartAt: new Date().getTime(),
        }));
        wavesurferRef.current.play();
        return;
      }

      // Different recording or no recording - stop current and start new
      stop();

      onReadyCallbackRef.current = onReady;
      analyticsCallbackRef.current = analyticsCallback;

      setState({
        recordingId,
        url,
        duration,
        currentPosition: 0,
        ready: false,
        playing: false,
        isWebAudioSupported,
        playStartAt: null,
      });

      // Use provided container or set flag to render hidden container
      if (container) {
        wavesurferRef.current = createWaveSurfer(url, duration, isWebAudioSupported, container);
      } else {
        setNeedsHiddenContainer(true);
      }
    },
    [state.recordingId, state.playing, stop, createWaveSurfer]
  );

  // Effect to create WaveSurfer in hidden container when needed
  useEffect(() => {
    if (needsHiddenContainer && hiddenContainerRef.current && !wavesurferRef.current && state.url) {
      wavesurferRef.current = createWaveSurfer(
        state.url,
        state.duration,
        state.isWebAudioSupported,
        hiddenContainerRef.current
      );
    }
  }, [needsHiddenContainer, state.url, state.duration, state.isWebAudioSupported, createWaveSurfer]);

  const pause = useCallback(() => {
    if (wavesurferRef.current && state.playing) {
      notifyAnalytics();
      wavesurferRef.current.pause();
      setState(prev => ({ ...prev, playing: false }));
    }
  }, [state.playing, notifyAnalytics]);

  const togglePlay = useCallback(() => {
    if (state.playing) {
      pause();
    } else if (wavesurferRef.current) {
      // If at end, restart from beginning
      let newPosition = state.currentPosition;
      if (state.currentPosition >= state.duration) {
        newPosition = 0;
      }
      setState(prev => ({
        ...prev,
        playing: true,
        playStartAt: new Date().getTime(),
        currentPosition: newPosition,
      }));
      wavesurferRef.current.play();
    }
  }, [state.playing, state.currentPosition, state.duration, pause]);

  const seek = useCallback(
    position => {
      if (wavesurferRef.current && state.duration > 0) {
        const clampedPosition = Math.max(0, Math.min(position, state.duration));
        setState(prev => ({ ...prev, currentPosition: clampedPosition }));
        wavesurferRef.current.seekTo(clampedPosition / state.duration);
      }
    },
    [state.duration]
  );

  const isPlayingRecording = useCallback(
    recordingId => {
      return state.recordingId === recordingId && state.playing;
    },
    [state.recordingId, state.playing]
  );

  const hasActiveRecording = useCallback(
    recordingId => {
      return state.recordingId === recordingId && wavesurferRef.current !== null;
    },
    [state.recordingId]
  );

  const attachToContainer = useCallback(
    (recordingId, container) => {
      if (state.recordingId !== recordingId || !wavesurferRef.current) {
        return false;
      }

      // Prevent concurrent attach operations
      if (isAttachingRef.current) {
        return false;
      }
      isAttachingRef.current = true;

      const oldWavesurfer = wavesurferRef.current;
      const wasPlaying = state.playing;
      const currentUrl = state.url;
      const currentDuration = state.duration;
      const currentIsWebAudioSupported = state.isWebAudioSupported;

      // Get current peaks for visualization
      let peaks = [];
      try {
        if (oldWavesurfer.backend?.getPeaks) {
          peaks = oldWavesurfer.backend.getPeaks(container.clientWidth || 300);
        }
      } catch (e) {
        // Ignore errors getting peaks
      }

      // Create new instance in the provided container while old one keeps playing
      const maxDuration = 3600;
      const useWebAudio = currentIsWebAudioSupported && currentDuration < maxDuration;

      const newWavesurfer = WaveSurfer.create({
        audioContext: useWebAudio ? null : 'OfflineAudioContext',
        backend: useWebAudio ? 'WebAudio' : 'MediaElement',
        barWidth: wavePlayerBarWidth,
        container,
        cursorColor: wavePlayerCursorColor,
        height: wavePlayerHeight,
        interact: true,
        mediaType: wavePlayerMediaType,
        progressColor: wavePlayerProgressColor,
        responsive: 150,
        waveColor: wavePlayerWaveColor,
      });

      // Set peaks if we have them for immediate visual
      if (peaks.length > 0) {
        try {
          newWavesurfer.backend.setPeaks(peaks);
          newWavesurfer.drawBuffer();
        } catch (e) {
          // Ignore errors setting peaks
        }
      }

      newWavesurfer.load(currentUrl);

      newWavesurfer.on('error', () => {
        // Reset attach lock if load fails so future attach attempts aren't blocked
        isAttachingRef.current = false;
        try {
          newWavesurfer.destroy();
        } catch (e) {
          // Ignore errors destroying failed instance
        }
      });

      newWavesurfer.on('ready', () => {
        // Get current position from old instance right before switching
        let currentPosition = state.currentPosition;
        try {
          if (oldWavesurfer?.getCurrentTime) {
            currentPosition = oldWavesurfer.getCurrentTime();
          }
        } catch (e) {
          // Use state position if getCurrentTime fails
        }

        if (currentDuration > 0) {
          newWavesurfer.seekTo(currentPosition / currentDuration);
        }

        if (wasPlaying) {
          newWavesurfer.play();
        }

        // Now destroy old instance
        try {
          oldWavesurfer._onResize?.clear();
          oldWavesurfer.destroy();
        } catch (e) {
          // Ignore errors destroying old instance
        }

        // Switch to new instance and register event listeners after old is destroyed
        wavesurferRef.current = newWavesurfer;
        newWavesurfer.on('finish', handleFinish);
        newWavesurfer.on('audioprocess', handlePositionChange);
        newWavesurfer.on('seek', handleSeek);

        setState(prev => ({ ...prev, currentPosition }));
        isAttachingRef.current = false;
        setNeedsHiddenContainer(false);
      });

      return true;
    },
    [
      state.recordingId,
      state.playing,
      state.url,
      state.duration,
      state.isWebAudioSupported,
      state.currentPosition,
      handleFinish,
      handlePositionChange,
      handleSeek,
    ]
  );

  const detachFromContainer = useCallback(recordingId => {
    // Nothing to do - let the existing WaveSurfer continue playing
    // The container DOM element is gone but audio playback continues
    // When attachToContainer is called, we'll create a fresh visual instance
  }, []);

  const value = useMemo(
    () => ({
      state,
      play,
      pause,
      stop,
      togglePlay,
      seek,
      isPlayingRecording,
      hasActiveRecording,
      attachToContainer,
      detachFromContainer,
    }),
    [
      state,
      play,
      pause,
      stop,
      togglePlay,
      seek,
      isPlayingRecording,
      hasActiveRecording,
      attachToContainer,
      detachFromContainer,
    ]
  );

  return (
    <AudioPlaybackContext.Provider value={value}>
      {children}
      {needsHiddenContainer && <HiddenAudioPlayer containerRef={hiddenContainerRef} />}
    </AudioPlaybackContext.Provider>
  );
}

AudioPlaybackProvider.propTypes = {
  children: PropTypes.node,
};

export function useAudioPlayback() {
  const context = useContext(AudioPlaybackContext);
  if (!context) {
    throw new Error('useAudioPlayback must be used within an AudioPlaybackProvider');
  }
  return context;
}

export default AudioPlaybackContext;
